import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControlLabel,
  Box,
  Typography,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import Switch from '@mui/material/Switch';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionSuggestField from './DescriptionSuggestField';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { utcToDisplayTimezone, displayTimezoneToUtc } from '../../utils/timezoneUtils';
import { API_BASE_URL, addMemberEmailToData } from '../../config/api';
import { useTheme } from '@mui/material/styles';
import useIsPhone from '../../hooks/useIsPhone';

import { useTeamspace, useOptionsForTeamspace } from '../../context/TeamspaceProvider';
import { useAuth } from '../../context/AuthContext';
import { isRestrictedStaff } from '../../config/staffRestrictions'; // Import useAuth

import axios from 'axios';
import { buildTagDescription, buildTagSuggestions, syncDescriptionWithTags } from '../../utils/tagDescription';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
// These functions are now in TeamspaceProvider.js
// const fetchMembersForAutocomplete = async (teamspaceId) => { ... };
// const fetchProjectsForAutocomplete = async (teamspaceId) => { ... };
// const fetchTagsForAutocomplete = async (teamspaceId) => { ... };

// Helper functions to convert between minutes and HH:MM format
const minutesToHHMM = (minutes) => {
  if (!minutes || minutes === 0) return '00:00';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const HHMMToMinutes = (hhmm) => {
  if (!hhmm || hhmm === '') return 0;
  // Handle different input formats
  const parts = hhmm.split(':');
  if (parts.length === 2) {
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    return hours * 60 + minutes;
  }
  // Fallback: treat as minutes if no colon
  return parseInt(hhmm) || 0;
};

// Helper function to extract hours from HH:MM format
const getHoursFromHHMM = (hhmm) => {
  if (!hhmm || hhmm === '') return 0;
  const parts = hhmm.split(':');
  if (parts.length === 2) {
    return parseInt(parts[0]) || 0;
  }
  return 0;
};

const postTimeEntry = async (timeEntryData, teamspaceId, user) => {
  timeEntryData.teamspace = teamspaceId
  // Add member_email to the request data
  const dataWithMemberEmail = addMemberEmailToData(timeEntryData, user);
  console.log('Sending time entry data to API:', dataWithMemberEmail);
  const apiUrl = `${API_BASE_URL}teamspaces/${teamspaceId}/time_entries/`; 
  try {
    const response = await axios.post(apiUrl, dataWithMemberEmail);

    if (response.status !== 201) { 
      const errorData = response.data;
      const error = new Error(`API error: ${JSON.stringify(errorData)}`);
      // Preserve response for error handling
      error.response = response;
      throw error;
    }

    return response.data;
  } catch (error) {
    console.error("Failed to post client data:", error);
    // Preserve original axios error response if it exists
    if (error.response) {
      throw error;
    }
    // If it's our custom error, make sure response is preserved
    throw error;
  }
};

const AddTimeEntryForm = ({ open, handleClose, onTimeEntryAdded, initialTimerData = null }) => {

  const {
    selectedTeamspaceId: globalSelectedTeamspaceId,
    teamspaces,
    teamspaceIdToNameMap,
    refreshTimeEntries,
    userPreferences,
    displayTimezone,
    startTimeEntryMutation,
    endTimeEntryMutation,
  } = useTeamspace();
  const { user, role_dict } = useAuth(); // Get user and role_dict from AuthContext
  const theme = useTheme();
  const isPhone = useIsPhone();

  // Local state for form's teamspace selection (independent from global)
  const [formTeamspaceId, setFormTeamspaceId] = useState(globalSelectedTeamspaceId);

  // Cached options for form's teamspace (loaded when teamspace initializes or when form teamspace changes)
  const formOptions = useOptionsForTeamspace(formTeamspaceId);
  const formProjects = formOptions.projects;
  const formMembers = formOptions.members;
  const formTags = formOptions.tags;
  const formCategories = formOptions.categories;
  const formMemberLookup = formOptions.memberLookup;
  const formTagLookup = formOptions.tagLookup;
  const formLookupLoading = formOptions.loading;

  // Determine if the current user is 'staff' in the form's selected teamspace
  const formTeamspaceName = teamspaceIdToNameMap[formTeamspaceId];
  const isStaff = user && formTeamspaceName && role_dict[formTeamspaceName]?.role?.includes('Staff');
  // DCC/PH Staff may log time for other members too (2026-10-08) — only the
  // entries they can see stay limited to their own.
  const canCreateForOthers = !!(user && formTeamspaceName && role_dict[formTeamspaceName]?.role?.some(r => ['Admin', 'Developer', 'Lead'].includes(r)))
    || isRestrictedStaff(user, role_dict, formTeamspaceName);
  
  // Find current user's member ID for staff users
  const currentUserMember = useMemo(() => {
    if (!user || !formMembers.length) return null;
    return formMembers.find(member => 
      member.email === user.email || 
      member.username === user.username ||
      member.label === user.username ||
      member.label === user.email
    );
  }, [user, formMembers]);
  
  // displayTimezone is now from context - always uses header's selectedTeamspaceId, not form's formTeamspaceId
  
  // Helper functions are now imported from utils/timezoneUtils.js
  // Wrapper functions that use displayTimezone from context for convenience
  const utcToDisplayTimezoneLocal = (utcDateString) => utcToDisplayTimezone(utcDateString, displayTimezone);
  const displayTimezoneToUtcLocal = (dayjsObj) => displayTimezoneToUtc(dayjsObj, displayTimezone);

  // Options for the form's teamspace come from cache (useOptionsForTeamspace); only fetched when teamspace changes in form or on first open


  const [error, setError] = useState(null);
  // Helper to round time to nearest 5 minutes (now optional, can be removed)
  const roundToNearestFiveMinutes = (time) => {
    return time.second(0);
  };

  // Calculate initial values - use timer data if provided, otherwise use defaults
  const getInitialFormData = () => {
    // Get default_billable from user preferences, default to true if not set
    const defaultBillable = userPreferences?.default_billable !== undefined ? userPreferences.default_billable : true;
    
    if (initialTimerData && initialTimerData.startTime && initialTimerData.endTime) {
      // Timer data provided - convert from UTC to display timezone
      const startDate = utcToDisplayTimezoneLocal(initialTimerData.startTime);
      const endDate = utcToDisplayTimezoneLocal(initialTimerData.endTime);
      const durationMinutes = Math.floor(initialTimerData.duration / 60);
      
      return {
        description: '',
        member: [], // array of member IDs
        project: '',
        category: '',
        tag: [], // ManyToMany relationship - array of tag IDs
        billable: defaultBillable,
        date: startDate,
        start_time: startDate,
        end_time: endDate,
        duration_minutes: durationMinutes,
        notes: '',
        teamspace: null
      };
    } else {
      // Default values for manual entry - get current time directly in display timezone
      // Ensure displayTimezone is valid before using it
      const validTimezone = displayTimezone || 'UTC';
      let nowInDisplayTz;
      try {
        if (validTimezone === 'UTC') {
          nowInDisplayTz = dayjs().utc();
        } else {
          // Use dayjs().tz() to get current time in the specified timezone
          nowInDisplayTz = dayjs().tz(validTimezone);
        }
        // Validate the date object
        if (!nowInDisplayTz || !nowInDisplayTz.isValid()) {
          throw new Error('Invalid date');
        }
      } catch (e) {
        // Fallback to UTC if timezone is invalid
        console.warn('Invalid timezone, falling back to UTC:', displayTimezone, e);
        nowInDisplayTz = dayjs().utc();
      }

      const initialDate = nowInDisplayTz;
      const initialStartTime = roundToNearestFiveMinutes(nowInDisplayTz);
      const initialEndTime = initialDate
        .hour(initialStartTime.hour())
        .minute(initialStartTime.minute())
        .second(0)
        .add(15, 'minute');
      
      return {
        description: '',
        member: [], // array of member IDs
        project: '',
        category: '',
        tag: [], // ManyToMany relationship - array of tag IDs
        billable: defaultBillable,
        date: initialDate,
        start_time: initialStartTime,
        end_time: initialEndTime,
        duration_minutes: 15,
        notes: '',
        teamspace: null
      };
    }
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [validationErrors, setValidationErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [durationInputValue, setDurationInputValue] = useState(null); // Track raw input value
  const [durationError, setDurationError] = useState(''); // Track duration validation error
  const durationInputRef = useRef(null); // Ref for duration input to preserve cursor position
  const durationPrevValueRef = useRef(null); // Track previous value for cursor calculation
  const memberEntriesRef = useRef([]); // Raw fetched entries for description suggestions
  const [expandedFields, setExpandedFields] = useState({
    member: false,
    project: false,
    tag: false,
  });
  const [showTeamspaceChangeDialog, setShowTeamspaceChangeDialog] = useState(false);
  const [pendingTeamspaceId, setPendingTeamspaceId] = useState(null);
  const [apiErrorDialogOpen, setApiErrorDialogOpen] = useState(false);
  const [billableSetByProject, setBillableSetByProject] = useState(false);
  const [billableOverriddenFromProject, setBillableOverriddenFromProject] = useState(false);
  const [duplicateCheckDialogOpen, setDuplicateCheckDialogOpen] = useState(false);
  const [duplicateCheckInfo, setDuplicateCheckInfo] = useState(null);
  const [descriptionSuggestions, setDescriptionSuggestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAutocompleteChange = (name) => (event, value) => {
    if (name === 'tag') {
      // Handle multiple tags
      const tagIds = Array.isArray(value) ? value.map(v => v.id) : (value ? [value.id] : []);
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: tagIds,
        // Keep a tag-derived description in sync with the new tags; a
        // description the user typed themselves is left untouched.
        description: syncDescriptionWithTags({
          description: prevFormData.description,
          previousTagIds: prevFormData.tag,
          nextTagIds: tagIds,
          tagLookup: formTagLookup,
        }),
      }));
    } else if (name === 'project') {
      // Handle project/category selection - mutually exclusive
      const defaultBillable = userPreferences?.default_billable !== undefined ? userPreferences.default_billable : true;
      if (value) {
        if (value.isCategory) {
          // Selected a category - set category and clear project; category entries are never billable
          setFormData(prevFormData => ({
            ...prevFormData,
            category: value.id,
            project: '',
            billable: false,
          }));
          setBillableSetByProject(false);
          setBillableOverriddenFromProject(false);
        } else {
          // Selected a project - set project, clear category, apply project's billable
          setFormData(prevFormData => ({
            ...prevFormData,
            project: value.id,
            category: '',
            billable: value.billable !== undefined ? value.billable : defaultBillable,
          }));
          setBillableSetByProject(true);
          setBillableOverriddenFromProject(false);
        }
      } else {
        // Cleared selection - clear both, revert billable to default
        setFormData(prevFormData => ({
          ...prevFormData,
          project: '',
          category: '',
          billable: defaultBillable,
        }));
        setBillableSetByProject(false);
        setBillableOverriddenFromProject(false);
      }
    } else if (name === 'member') {
      // Handle multiple members
      const memberIds = Array.isArray(value) ? value.map(v => v.id) : (value ? [value.id] : []);
      setFormData(prevFormData => ({
        ...prevFormData,
        member: memberIds
      }));
    } else {
      // Handle single value fields
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: value ? value.id : ''
      }));
    }
  };

  const handleDateTimeChange = (name) => (newValue) => {
    if (name === 'start_time') {
      setFormData(prevFormData => {
        const updated = { ...prevFormData, start_time: newValue };
        // If end_time exists, recalculate duration
        if (updated.end_time && updated.end_time.isValid() && newValue && newValue.isValid()) {
          const startDateTime = updated.date
            .hour(newValue.hour())
            .minute(newValue.minute())
            .second(0);
          let endDateTime = updated.date
            .hour(updated.end_time.hour())
            .minute(updated.end_time.minute())
            .second(0);
          let diffMinutes = endDateTime.diff(startDateTime, 'minute');
          // Handle case where end_time is before start_time (next day)
          if (diffMinutes < 0) {
            endDateTime = endDateTime.add(1, 'day');
            diffMinutes = endDateTime.diff(startDateTime, 'minute');
          }
          updated.duration_minutes = Math.max(diffMinutes, 0);
        } else if (!updated.end_time || !updated.end_time.isValid()) {
          // If end_time doesn't exist, set duration to 0
          updated.duration_minutes = 0;
        }
        return updated;
      });
    } else if (name === 'end_time') {
      setFormData(prevFormData => {
        const updated = { ...prevFormData, end_time: newValue };
        // If end_time is set and start_time exists, calculate duration
        if (newValue && newValue.isValid() && updated.start_time && updated.start_time.isValid()) {
          const startDateTime = updated.date
            .hour(updated.start_time.hour())
            .minute(updated.start_time.minute())
            .second(0);
          let endDateTime = updated.date
            .hour(newValue.hour())
            .minute(newValue.minute())
            .second(0);
          let diffMinutes = endDateTime.diff(startDateTime, 'minute');
          if (diffMinutes < 0) {
            endDateTime = endDateTime.add(1, 'day');
            diffMinutes = endDateTime.diff(startDateTime, 'minute');
          }
          updated.duration_minutes = Math.max(diffMinutes, 0);
        } else if (!newValue) {
          // If end_time is cleared, don't change duration
        }
        return updated;
      });
    } else {
      setFormData(prevFormData => ({
        ...prevFormData,
        [name]: newValue
      }));
    }
  };

  // Handle duration change - update end_time
  const handleDurationChange = (inputValue) => {
    // If empty, allow it during typing but don't update formData yet
    if (inputValue === '' || inputValue.trim() === '') {
      setDurationInputValue('');
      return;
    }
    // HHMMToMinutes already handles minutes > 59 correctly (e.g., 01:80 = 140 minutes)
    const minutes = HHMMToMinutes(inputValue);
    const clampedValue = Math.min(Math.max(minutes, 0), 1440); // 24 hours max
    setDurationInputValue(null); // Clear raw input, use normalized value
    
    setFormData(prev => {
      const updated = { ...prev, duration_minutes: clampedValue };
      // If start_time exists, calculate end_time
      if (updated.start_time && updated.start_time.isValid() && clampedValue > 0) {
        const startDateTime = updated.date
          .hour(updated.start_time.hour())
          .minute(updated.start_time.minute())
          .second(0);
        updated.end_time = startDateTime.add(clampedValue, 'minute');
      } else {
        updated.end_time = null;
      }
      return updated;
    });
  };

  const toggleField = (field) => {
    setExpandedFields(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getSelectedLabel = (field, options, value) => {
    if (field === 'member') {
      // Handle array of member IDs
      if (!value || (Array.isArray(value) && value.length === 0)) return null;
      const memberIds = Array.isArray(value) ? value : [value];
      if (memberIds.length === 0) return null;
      if (memberIds.length === 1) {
        const option = options.find(o => String(o.id) === String(memberIds[0]));
        return option ? option.label : null;
      }
      return `(${memberIds.length} members)`;
    }
    if (field === 'tag' || field === 'tags') {
      // Handle array of tag IDs
      if (!value || (Array.isArray(value) && value.length === 0)) return null;
      const tagIds = Array.isArray(value) ? value : [value];
      if (tagIds.length === 0) return null;
      if (tagIds.length === 1) {
        const option = options.find(o => o.id === tagIds[0]);
        return option ? option.label : null;
      }
      return `(${tagIds.length} tags)`;
    }
    if (field === 'project') {
      // Handle project or category selection
      if (!value && !formData.category) return null;
      // Check category first
      if (formData.category) {
        const categoryOption = formCategories.find(c => c.id === formData.category);
        if (categoryOption) return categoryOption.label;
      }
      // Then check project
      if (value) {
        const option = options.find(o => o.id === value);
        return option ? option.label : null;
      }
      return null;
    }
    if (!value) return null;
    const option = options.find(o => o.id === value);
    return option ? option.label : null;
  };




  const validateForm = () => {
    console.log("Validating form...", formData);
    const errors = {};
    // Description is optional
    // Check if member is set - for staff, it should be set automatically, for non-staff it's required
    const memberIsEmpty = !formData.member || (Array.isArray(formData.member) && formData.member.length === 0);
    if (formLookupLoading && memberIsEmpty) {
      // If members are still loading and member is not set, wait
      errors.member = 'Loading member information...';
    } else if (canCreateForOthers && memberIsEmpty) {
      errors.member = 'Member is required.';
    } else if (!canCreateForOthers && !isStaff && memberIsEmpty) {
      errors.member = 'Member is required.';
    } else if (!canCreateForOthers && isStaff && memberIsEmpty && !currentUserMember) {
      // For staff, if member is not set and currentUserMember is not available, wait
      errors.member = 'Loading member information...';
    }
    // Project is required in the UI even though time_entry.project is nullable.
    // The field is a single combined picker, so a time entry category satisfies it too.
    if (!formData.project && !formData.category) {
      errors.project = 'Project is required.';
    }
    
    // Validate date
    if (!formData.date || !formData.date.isValid()) {
      errors.date = 'Date is required and must be valid.';
    }
    // Validate start time
    if (!formData.start_time || !formData.start_time.isValid()) {
      errors.start_time = 'Start time is required and must be valid.';
    }
    // Validate duration - optional, but if provided must be valid
    // Also check if either duration or end_time is provided
    const hasDuration = formData.duration_minutes && formData.duration_minutes > 0;
    const hasEndTime = formData.end_time && formData.end_time.isValid();
    
    if (!hasDuration && !hasEndTime) {
      // Both are optional, so no error
    } else if (formData.duration_minutes && formData.duration_minutes < 0) {
      errors.duration_minutes = 'Duration cannot be negative.';
    }
    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    console.log("Validation errors:", errors);
    console.log("Form is valid:", isValid);
    return isValid;
  };

  // Returns the subset of memberIds that already have an identical entry in the teamspace.
  const checkForDuplicates = async (memberIds, startDateUTC, endDateUTC, basePayload) => {
    try {
      const params = new URLSearchParams({
        start_date: startDateUTC,
        end_date: startDateUTC, // exact match: entries whose start_time == startDateUTC
        timezone: 'UTC',
        page_size: 200,
      });
      const response = await axios.get(
        `${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/?${params.toString()}`
      );
      const entries = Array.isArray(response.data) ? response.data : (response.data.results || []);
      const targetEndMs = endDateUTC ? new Date(endDateUTC).getTime() : null;

      return memberIds.filter(memberId =>
        entries.some(entry => {
          if (String(entry.member) !== String(memberId)) return false;
          // end_time
          const entryEndMs = entry.end_time ? new Date(entry.end_time).getTime() : null;
          if (entryEndMs !== targetEndMs) return false;
          // project
          if (basePayload.project) {
            if (String(entry.project) !== String(basePayload.project)) return false;
          } else if (entry.project) return false;
          // category
          if (basePayload.category) {
            if (String(entry.category) !== String(basePayload.category)) return false;
          } else if (entry.category) return false;
          // description
          if ((entry.description || '') !== (basePayload.description || '')) return false;
          return true;
        })
      );
    } catch (err) {
      console.warn('Duplicate check failed, proceeding without check:', err);
      return []; // fail open — don't block the user
    }
  };

  // Executes the actual creation loop after the duplicate check has been resolved.
  const doCreateEntries = async (memberIds, basePayload, keepOpen = false) => {
    setIsSubmitting(true);
    startTimeEntryMutation();
    try {
      const createdEntries = [];
      for (const memberId of memberIds) {
        const payload = { ...basePayload, member: memberId };
        console.log('Submitting time entry payload:', payload);
        const result = await postTimeEntry(payload, formTeamspaceId, user);
        console.log('Time Entry successfully created - full response:', result);
        const timeEntryData = result.time_entry || result;
        createdEntries.push(timeEntryData);
      }

      // Notify parent for each created entry so all are added to the list/calendar.
      // keepOpen tells the parent not to close the dialog ("Save and add another").
      for (const entry of createdEntries) {
        onTimeEntryAdded(entry, { keepOpen });
      }

      // Success feedback + form reset back to the same blank state the dialog opens with
      const savedCount = createdEntries.length;
      setSuccessMessage(
        keepOpen
          ? `${savedCount} time ${savedCount === 1 ? 'entry' : 'entries'} added. Form cleared - add another.`
          : 'Time entry added successfully'
      );
      setFormData({
        ...buildBlankFormData(),
        member: resolveDefaultMemberIds(),
      });
      setDurationInputValue(null);
      setDurationError('');
      durationPrevValueRef.current = null;
      setValidationErrors({});
      setError(null);
      setBillableSetByProject(false);
      setBillableOverriddenFromProject(false);
      setExpandedFields({ member: false, project: false, tag: false });
      if (refreshTimeEntries) {
        refreshTimeEntries();
      }
    } catch (err) {
      console.error('Error creating time entry:', err);
      console.error('Error details:', err.message);
      console.error('Error response:', err.response?.data);
      const isNetworkError = !err.response && (
        err.message?.includes('Network Error') ||
        err.code === 'ERR_CONNECTION_REFUSED' ||
        err.code === 'ERR_NETWORK' ||
        err.message?.includes('ERR_CONNECTION_REFUSED')
      );
      const responseStatus = err.response?.status;
      if (isNetworkError || (responseStatus && responseStatus !== 200 && responseStatus !== 201)) {
        setApiErrorDialogOpen(true);
        return;
      }
      let errorMessage = err.message;
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.response.data.member) {
          errorMessage = `Member error: ${Array.isArray(err.response.data.member) ? err.response.data.member.join(', ') : err.response.data.member}`;
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      }
      setError(errorMessage);
      setValidationErrors(err.response?.data || {});
    } finally {
      endTimeEntryMutation();
      setIsSubmitting(false);
    }
  };

  // keepOpen = true when the user pressed "Save and add another": the entry is
  // created, the form is cleared, and the dialog stays open for the next one.
  const submitForm = async (keepOpen) => {
    if (isSubmitting) return;
    console.log("Submit button clicked. Attempting to validate form...");
    if (validateForm()) {
      await performSubmit(keepOpen);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await submitForm(false);
  };

  const handleSaveAndAddAnother = async () => {
    await submitForm(true);
  };

  const performSubmit = async (keepOpen = false) => {
    console.log("Form is valid. Attempting to submit...");
    try {
      // Combine date with times to create full datetime objects
      // These are in displayTimezone (teamspace member role timezone if use_device_timezone is false,
      // or device timezone if use_device_timezone is true)
      // Create a dayjs object explicitly in displayTimezone to ensure proper conversion
      // IMPORTANT: If timezone is UTC, no conversion needed
      const dateTimeStr = `${formData.date.format('YYYY-MM-DD')}T${formData.start_time.format('HH:mm:ss')}`;
      const startDateTime = (!displayTimezone || displayTimezone === 'UTC')
        ? dayjs.utc(dateTimeStr)
        : dayjs.tz(dateTimeStr, displayTimezone);
      
      // Convert from displayTimezone to UTC before submitting to API
      // displayTimezone is determined by use_device_timezone preference:
      // - If false: teamspace member role timezone
      // - If true: device timezone (automatic detection)
      const startDateUTC = displayTimezoneToUtcLocal(startDateTime);
      
      // Determine end_time: use formData.end_time if provided, otherwise calculate from duration
      let endDateUTC;
      if (formData.end_time && formData.end_time.isValid()) {
        // Build end datetime on same date as start; if earlier, roll to next day
        // Create a dayjs object explicitly in displayTimezone
        // IMPORTANT: If timezone is UTC, no conversion needed
        const endDateTimeStr = `${formData.date.format('YYYY-MM-DD')}T${formData.end_time.format('HH:mm:ss')}`;
        let endDateTime = (!displayTimezone || displayTimezone === 'UTC')
          ? dayjs.utc(endDateTimeStr)
          : dayjs.tz(endDateTimeStr, displayTimezone);
        if (endDateTime.isBefore(startDateTime)) {
          endDateTime = endDateTime.add(1, 'day');
        }
        endDateUTC = displayTimezoneToUtcLocal(endDateTime);
      } else if (formData.duration_minutes && formData.duration_minutes > 0) {
        const durationSeconds = formData.duration_minutes * 60;
        const startDate = new Date(startDateUTC);
        const endDate = new Date(startDate.getTime() + (durationSeconds * 1000));
        endDateUTC = endDate.toISOString();
      } else {
        // If neither provided, we need at least one - validation should catch this
        throw new Error('Either duration or end time must be provided.');
      }

      // Resolve member IDs array from formData.member
      let memberIds = Array.isArray(formData.member) ? [...formData.member] : (formData.member ? [formData.member] : []);

      // If members are still loading, throw an error
      if (formLookupLoading) {
        throw new Error('Member information is still loading. Please wait a moment and try again.');
      }

      // If no members selected, try fallback to current user
      if (memberIds.length === 0) {
        let fallbackId = null;
        if (!canCreateForOthers && isStaff && currentUserMember) {
          fallbackId = currentUserMember.id;
        } else if (!canCreateForOthers && isStaff && !currentUserMember && formMembers.length > 0) {
          const foundMember = formMembers.find(member =>
            member.email === user?.email ||
            member.username === user?.username ||
            member.label === user?.username ||
            member.label === user?.email
          );
          if (foundMember) fallbackId = foundMember.id;
        } else if ((canCreateForOthers || !isStaff) && (memberOptions.length > 0 || formMembers.length > 0)) {
          const allMembers = memberOptions.length > 0 ? memberOptions : formMembers;
          const foundMember = allMembers.find(m =>
            m.email === user?.email ||
            m.username === user?.username ||
            m.label === user?.username ||
            m.label === user?.email
          );
          if (foundMember) fallbackId = foundMember.id;
        }
        if (!fallbackId && (canCreateForOthers || !isStaff)) {
          throw new Error('Member is required. Please select a member.');
        }
        if (!fallbackId && !canCreateForOthers && isStaff) {
          throw new Error('Member information not yet loaded. Please wait a moment and try again.');
        }
        if (fallbackId) memberIds = [fallbackId];
      }

      // Validate each member ID exists in current teamspace
      if (memberIds.length > 0 && (memberOptions.length > 0 || formMembers.length > 0)) {
        const validatedIds = [];
        for (const memberId of memberIds) {
          const memberExists = memberOptions.some(m => m.id === memberId || String(m.id) === String(memberId)) ||
                               formMembers.some(m => String(m.id) === String(memberId));
          if (!memberExists) {
            console.warn('⚠️ Member ID not found in current teamspace, skipping:', memberId);
            continue;
          }
          validatedIds.push(memberId);
        }
        if (validatedIds.length === 0) {
          throw new Error('Member ID is not valid for the selected teamspace. Please wait for member information to load and try again.');
        }
        memberIds = validatedIds;
      } else if (memberIds.length > 0 && formMembers.length === 0 && memberOptions.length === 0) {
        console.warn('⚠️ Member IDs set but no members loaded yet.');
        throw new Error('Member information is still loading. Please wait a moment and try again.');
      }

      // Build base payload (without member)
      const basePayload = {
        description: formData.description || '',
        billable: Boolean(formData.billable) && !formData.category,
        start_time: startDateUTC,
        end_time: endDateUTC,
        notes: formData.notes || '',
        created_by: currentUserMember?.id,
        lastedited_by: currentUserMember?.id,
      };

      // Include category or project (mutually exclusive)
      if (formData.category) {
        basePayload.category = formData.category;
      } else if (formData.project) {
        basePayload.project = parseInt(formData.project, 10);
      }

      console.log('=== ADD TIME ENTRY - TIME CONVERSION ===');
      console.log('Display Timezone:', displayTimezone);
      console.log('User Input (Display Timezone):', {
        date: formData.date.format('YYYY-MM-DD'),
        start_time: formData.start_time.format('HH:mm'),
        end_time: formData.end_time ? formData.end_time.format('HH:mm') : 'N/A',
        duration_minutes: formData.duration_minutes
      });
      const endDateTimeDisplay = formData.end_time && formData.end_time.isValid()
        ? ((!displayTimezone || displayTimezone === 'UTC')
            ? dayjs.utc(`${formData.date.format('YYYY-MM-DD')}T${formData.end_time.format('HH:mm:ss')}`)
            : dayjs.tz(`${formData.date.format('YYYY-MM-DD')}T${formData.end_time.format('HH:mm:ss')}`, displayTimezone))
        : null;
      console.log('Full DateTime Objects (Display Timezone):', {
        startDateTime: startDateTime.format('YYYY-MM-DD HH:mm:ss [') + startDateTime.format('z') + ']',
        endDateTime: endDateTimeDisplay ? endDateTimeDisplay.format('YYYY-MM-DD HH:mm:ss [') + endDateTimeDisplay.format('z') + ']' : 'N/A'
      });
      console.log('Converted to UTC (API Payload):', {
        start_time: basePayload.start_time,
        end_time: basePayload.end_time,
        duration_minutes: formData.duration_minutes
      });
      console.log('==========================================');

      const trimmedDescription = formData.description?.trim();

      if (!trimmedDescription) {
        // Use all selected tag names as description if description is empty
        const tagDescription = buildTagDescription(formData.tag, formTagLookup);
        if (tagDescription) {
          basePayload.description = tagDescription;
        }
      }

      // Tag handling
      if (formData.tag && Array.isArray(formData.tag) && formData.tag.length > 0) {
        basePayload.tag = formData.tag.map(id => parseInt(id, 10));
      }

      console.log('Submitting time entries for members:', memberIds);

      // Check for duplicate entries before creating
      const duplicateMemberIds = await checkForDuplicates(memberIds, startDateUTC, endDateUTC, basePayload);

      if (duplicateMemberIds.length > 0) {
        setDuplicateCheckInfo({
          duplicateMemberIds,
          nonDuplicateMemberIds: memberIds.filter(id => !duplicateMemberIds.map(String).includes(String(id))),
          allMemberIds: memberIds,
          basePayload,
          keepOpen,
        });
        setDuplicateCheckDialogOpen(true);
        return; // Wait for user confirmation via dialog
      }

      await doCreateEntries(memberIds, basePayload, keepOpen);

    } catch (err) {
      // Handle setup/preparation errors (member resolution, time calculation, etc.)
      console.error('Error preparing time entry:', err);
      setError(err.message);
    }
  };


  // Convert lookup maps to array of options for Autocomplete components
  const availableProjects = useMemo(() => {
    return formProjects
      .filter(p => !p.is_archived) // Exclude archived projects
      .map(p => ({ id: p.id, label: p.full_project_name || p.name || '', isCategory: false, billable: p.billable }))
      .sort((a, b) => {
        const labelA = (a.label || '').toLowerCase();
        const labelB = (b.label || '').toLowerCase();
        return labelA.localeCompare(labelB);
      });
  }, [formProjects]);
  
  // Combine categories (first) and projects for the project dropdown
  const projectAndCategoryOptions = useMemo(() => {
    // Categories first, then projects
    return [...formCategories, ...availableProjects];
  }, [formCategories, availableProjects]);
  
  const memberOptions = useMemo(() => Object.entries(formMemberLookup).map(([id, label]) => ({ id, label })), [formMemberLookup]);
  const tagOptions = useMemo(() => Object.entries(formTagLookup).map(([id, label]) => ({ id, label })), [formTagLookup]);

  // A pristine manual-entry form: "now" in the display timezone, 15 minutes long,
  // billable per the user's preference. Shared by the on-open reset and by the
  // reset that follows "Save and add another" so both leave the same blank form.
  const buildBlankFormData = () => {
    const defaultBillable = userPreferences?.default_billable !== undefined ? userPreferences.default_billable : true;
    const validTimezone = displayTimezone || 'UTC';
    let nowInDisplayTz;
    try {
      nowInDisplayTz = (validTimezone === 'UTC') ? dayjs().utc() : dayjs().tz(validTimezone);
      if (!nowInDisplayTz || !nowInDisplayTz.isValid()) {
        throw new Error('Invalid date');
      }
    } catch (e) {
      console.warn('Invalid timezone, falling back to UTC:', displayTimezone, e);
      nowInDisplayTz = dayjs().utc();
    }

    const initialDate = nowInDisplayTz;
    const initialStartTime = roundToNearestFiveMinutes(nowInDisplayTz);
    const initialEndTime = initialDate
      .hour(initialStartTime.hour())
      .minute(initialStartTime.minute())
      .second(0)
      .add(15, 'minute');

    return {
      description: '',
      member: [],
      project: '',
      category: '',
      tag: [],
      billable: defaultBillable,
      date: initialDate,
      start_time: initialStartTime,
      end_time: initialEndTime,
      duration_minutes: 15,
      notes: '',
      teamspace: null,
    };
  };

  // Who the member field should default to: staff are locked to themselves,
  // everyone else starts on themselves and may change it.
  const resolveDefaultMemberIds = (fallback = []) => {
    const usernameToMatch = user?.username || user?.email;
    const currentMember = memberOptions.find(m => m.label === usernameToMatch);
    // resolvedSelfId: prefer the label-matched member (currentMember), fall back to email-matched member
    // (currentUserMember) for users whose username is empty or whose email_alt differs from their PK email.
    const resolvedSelfId = currentMember?.id ?? currentUserMember?.id ?? null;
    if (!canCreateForOthers && isStaff && currentUserMember) return [currentUserMember.id];
    if (resolvedSelfId) return [resolvedSelfId];
    return fallback;
  };
  
  

  // Effect to reset form to defaults when modal opens (only when dialog opens, not when teamspace changes)
  const prevOpenRef = useRef(open);
  useEffect(() => {
    // Only reset when dialog first opens (open changes from false to true)
    if (open && !prevOpenRef.current) {
      // Get default_billable from user preferences, default to true if not set
      const defaultBillable = userPreferences?.default_billable !== undefined ? userPreferences.default_billable : true;

      // Use timer data if provided, otherwise use defaults
      let initialData;
      if (initialTimerData && initialTimerData.startTime && initialTimerData.endTime) {
        // Timer data provided - use it
        const startDate = dayjs(initialTimerData.startTime);
        const endDate = dayjs(initialTimerData.endTime);
        const durationMinutes = Math.floor(initialTimerData.duration / 60);
        
        initialData = {
          description: '',
          member: [],
          project: '',
          category: '',
          tag: [],
          billable: defaultBillable,
          date: startDate,
          start_time: startDate,
          end_time: endDate,
          duration_minutes: durationMinutes,
          notes: '',
          teamspace: null
        };
      } else {
        // Default values for manual entry - "now" in the display timezone
        initialData = buildBlankFormData();
      }

      // For staff (non-privileged) users, lock to their own member; everyone else defaults to themselves
      const defaultMember = resolveDefaultMemberIds(initialData.member);

      setFormData({
        ...initialData,
        member: defaultMember,
      });
      setValidationErrors({});
      setError(null);
      setSuccessMessage('');
      setBillableSetByProject(false);
      setBillableOverriddenFromProject(false);
      setDurationInputValue(null); // Reset duration input value
      setDurationError(''); // Reset duration error
      durationPrevValueRef.current = null; // Reset previous value ref
      // For staff users, automatically expand member field to show it's filled and locked
      setExpandedFields({ member: false, project: false, tag: false });
    }
    prevOpenRef.current = open;
  }, [open, user, isStaff, initialTimerData, currentUserMember, memberOptions, userPreferences]);

  // The success banner stays only long enough to be read - with the dialog kept
  // open by "Save and add another" a permanent banner would outlive its entry.
  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => setSuccessMessage(''), 4000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  // Rescue effect: if the form is open but the member field ended up empty (e.g. because
  // memberOptions wasn't ready when the reset fired), fill it in once data is available.
  useEffect(() => {
    if (open && !formLookupLoading && formMembers.length > 0 && formTeamspaceId && user) {
      const foundMember = formMembers.find(member =>
        member.email === user?.email ||
        member.username === user?.username ||
        member.label === user?.username ||
        member.label === user?.email
      );
      if (foundMember && formData.member.length === 0) {
        setFormData(prev => ({ ...prev, member: [String(foundMember.id)] }));
      }
    }
  }, [formLookupLoading, formMembers, user, formTeamspaceId, open, formData.member]);

  // Initialize form teamspace when dialog opens - always default to current global teamspace
  const prevOpenRefForTeamspace = useRef(open);
  useEffect(() => {
    // When dialog opens (open changes from false to true), reset to global teamspace
    if (open && !prevOpenRefForTeamspace.current && globalSelectedTeamspaceId) {
      setFormTeamspaceId(globalSelectedTeamspaceId);
    }
    prevOpenRefForTeamspace.current = open;
  }, [open, globalSelectedTeamspaceId]);

  // Tag names offered alongside the recently-used descriptions, selected tags first
  const tagSuggestions = useMemo(
    () => buildTagSuggestions({ selectedTagIds: formData.tag, tagLookup: formTagLookup }),
    [formData.tag, formTagLookup]
  );

  // Helper: compute suggestions from raw entries based on project/category
  const computeDescriptionSuggestions = (entries, projectId, categoryId, memberId) => {
    const memberEntries = entries.filter(e => String(e.member) === String(memberId));
    let filtered = memberEntries;
    if (projectId || categoryId) {
      const projectFiltered = memberEntries.filter(e =>
        projectId ? String(e.project) === String(projectId) : String(e.category) === String(categoryId)
      );
      if (projectFiltered.length > 0) filtered = projectFiltered;
    }
    const seen = new Set();
    const result = [];
    for (const entry of filtered) {
      const desc = entry.description?.trim();
      if (desc && !seen.has(desc)) { seen.add(desc); result.push(desc); }
    }
    return result.slice(0, 20);
  };

  // Fetch recent entries for description suggestions when form opens or teamspace/member changes
  useEffect(() => {
    memberEntriesRef.current = [];
    setDescriptionSuggestions([]);
    if (!open || !formTeamspaceId || !currentUserMember?.id) return;
    const fetchDescriptions = async () => {
      try {
        const params = new URLSearchParams({ page_size: 100, member: currentUserMember.id, is_optimized: 'true' });
        const response = await axios.get(
          `${API_BASE_URL}teamspaces/${formTeamspaceId}/time_entries/?${params.toString()}`
        );
        const entries = Array.isArray(response.data) ? response.data : (response.data.results || []);
        memberEntriesRef.current = entries;
        setDescriptionSuggestions(
          computeDescriptionSuggestions(entries, formData.project, formData.category, currentUserMember.id)
        );
      } catch (err) {
        console.warn('Failed to fetch description suggestions:', err);
      }
    };
    fetchDescriptions();
  }, [open, formTeamspaceId, currentUserMember]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute suggestions when project/category selection changes
  useEffect(() => {
    if (!currentUserMember?.id || !memberEntriesRef.current.length) return;
    setDescriptionSuggestions(
      computeDescriptionSuggestions(memberEntriesRef.current, formData.project, formData.category, currentUserMember.id)
    );
  }, [formData.project, formData.category]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track previous teamspace to detect changes
  const prevFormTeamspaceIdRef = useRef(formTeamspaceId);
  const teamspaceJustChangedRef = useRef(false);
  const prevFormMembersLengthRef = useRef(0);
  const teamspaceIdWhenMembersLoadedRef = useRef(null);
  const prevDisplayTimezoneRef = useRef(displayTimezone);

  // Check if any fields that will be reset are selected
  const hasFieldsToReset = () => {
    // Check if member is set to only the current user (not by ID, since IDs differ per teamspace)
    let memberIsCurrentUser = false;
    const memberArray = Array.isArray(formData.member) ? formData.member : (formData.member ? [formData.member] : []);

    if (memberArray.length > 0 && user) {
      if (memberArray.length > 1) {
        // Multiple members selected - not just current user
        memberIsCurrentUser = false;
      } else {
        const memberId = memberArray[0];
        // Method 1: Check using formMembers (preferred - checks by email)
        if (formMembers.length > 0) {
          const currentMember = formMembers.find(m => String(m.id) === String(memberId));
          if (currentMember) {
            memberIsCurrentUser = currentMember.email === user.email ||
                                  currentMember.email?.toLowerCase() === user.email?.toLowerCase();
          }
        }
        // Method 2: Fallback - check using currentUserMember
        if (!memberIsCurrentUser && currentUserMember) {
          memberIsCurrentUser = String(memberId) === String(currentUserMember.id);
        }
        // Method 3: Fallback - check if member ID matches any member with user's email
        if (!memberIsCurrentUser && formMembers.length > 0) {
          const userMember = formMembers.find(m =>
            m.email === user.email ||
            m.email?.toLowerCase() === user.email?.toLowerCase() ||
            m.username === user.username ||
            m.label === user.username ||
            m.label === user.email
          );
          if (userMember && String(memberId) === String(userMember.id)) {
            memberIsCurrentUser = true;
          }
        }
      }
    }

    // Don't count member if it's only the current user AND project, category, and tag are empty
    const hasMember = memberArray.length > 0 && !memberIsCurrentUser;
    const hasProject = formData.project && formData.project !== '';
    const hasCategory = formData.category && formData.category !== '';
    const hasTag = formData.tag && Array.isArray(formData.tag) && formData.tag.length > 0;
    
    // Debug logging
    if (memberArray.length > 0) {
      console.log('🔍 Checking if confirmation needed:');
      console.log('   Member IDs:', memberArray);
      console.log('   Member is current user:', memberIsCurrentUser);
      console.log('   Has project:', hasProject);
      console.log('   Has category:', hasCategory);
      console.log('   Has tag:', hasTag);
      console.log('   Will show confirmation:', hasMember || hasProject || hasCategory || hasTag);
    }
    
    // Only show confirmation if member is not current user, OR if project/category/tag are set
    return hasMember || hasProject || hasCategory || hasTag;
  };

  // Handle teamspace change with confirmation if needed
  const handleTeamspaceChange = (newTeamspaceId) => {
    if (hasFieldsToReset()) {
      setPendingTeamspaceId(newTeamspaceId);
      setShowTeamspaceChangeDialog(true);
    } else {
      setFormTeamspaceId(newTeamspaceId);
    }
  };

  // Handle confirmation dialog actions
  const handleConfirmTeamspaceChange = () => {
    if (pendingTeamspaceId) {
      setFormTeamspaceId(pendingTeamspaceId);
      setPendingTeamspaceId(null);
    }
    setShowTeamspaceChangeDialog(false);
  };

  const handleCancelTeamspaceChange = () => {
    setPendingTeamspaceId(null);
    setShowTeamspaceChangeDialog(false);
  };

  // Effect to clear member, project, and tag when form teamspace changes
  // Only reset member, project, and tag fields - keep all other fields (description, billable, date, time, duration) unchanged
  // Timezone conversion happens in a separate effect after members load
  useEffect(() => {
    // Only reset if teamspace actually changed (not on initial mount or dialog open)
    if (open && formTeamspaceId && prevFormTeamspaceIdRef.current !== formTeamspaceId && prevFormTeamspaceIdRef.current !== null) {
      console.log('🔄 Teamspace changed from', prevFormTeamspaceIdRef.current, 'to', formTeamspaceId);
      
      teamspaceJustChangedRef.current = true; // Mark that teamspace just changed
      
      // Store the current displayTimezone as the old timezone (before members reload)
      // This will be used for conversion after new members load
      if (!userPreferences?.use_device_timezone) {
        // Store current timezone as old timezone for later conversion
        // displayTimezone will update after formMembers reloads
      }
      
      // Clear the ref that tracks which teamspace members belong to
      // This ensures we wait for NEW teamspace's members to load
      teamspaceIdWhenMembersLoadedRef.current = null;
      prevFormMembersLengthRef.current = 0;
      
      // Clear member, project, category, and tag fields immediately
      setFormData(prev => ({
        ...prev,
        member: [], // Clear member field - will be set to NEW teamspace's member once loaded
        project: '',
        category: '',
        tag: [],
        // Keep all other fields unchanged: description, billable, date, start_time, end_time, duration_minutes
      }));
      
      setExpandedFields({ member: false, project: false, tag: false });
      setError(null); // Clear any previous errors
      setValidationErrors({}); // Clear validation errors
      
      // Note: formMembers will be cleared and repopulated by the fetchFormData effect
      // The member will be set to current user from the NEW teamspace once new members are loaded
      // Timezone conversion will happen in the displayTimezone change effect after members load
    }
    // Update ref to track current teamspace
    prevFormTeamspaceIdRef.current = formTeamspaceId;
  }, [formTeamspaceId, open, userPreferences?.use_device_timezone]);

  // Effect to set member to current user after teamspace change and members are loaded
  // CRITICAL: Always sets member to current user from the NEW teamspace (not the old one)
  // Each teamspace has unique member instances with different IDs for the same user
  const prevTeamspaceForMemberRef = useRef(formTeamspaceId);
  useEffect(() => {
    // Track when members finish loading for a teamspace
    if (!formLookupLoading && formMembers.length > 0 && formTeamspaceId) {
      teamspaceIdWhenMembersLoadedRef.current = formTeamspaceId;
    }
    
    // Check if teamspace actually changed
    const teamspaceChanged = prevTeamspaceForMemberRef.current !== formTeamspaceId && prevTeamspaceForMemberRef.current !== null;
    
    // CRITICAL: Run whenever teamspace changed AND members are loaded for the NEW teamspace
    // We don't care about length changes - we just need members loaded for current teamspace
    const membersBelongToCurrentTeamspace = teamspaceIdWhenMembersLoadedRef.current === formTeamspaceId;
    
    // Run if teamspace changed (via flag OR direct comparison) AND members are ready
    if ((teamspaceJustChangedRef.current || teamspaceChanged) && 
        !formLookupLoading && 
        formMembers.length > 0 && 
        user && 
        formTeamspaceId &&
        membersBelongToCurrentTeamspace) {
      
      console.log('🔄 Teamspace changed. Finding current user in NEW teamspace members');
      console.log('   Current teamspace:', formTeamspaceId);
      console.log('   Members loaded for teamspace:', teamspaceIdWhenMembersLoadedRef.current);
      console.log('   Members belong to current teamspace:', membersBelongToCurrentTeamspace);
      console.log('   All members in NEW teamspace:', formMembers.map(m => ({ id: m.id, label: m.label, email: m.email })));
      
      // Find current user in the NEW teamspace's members
      // formMembers now contains members from the NEW teamspace (different IDs than old teamspace)
      const usernameToMatch = user?.username || user?.email;
      const foundMember = formMembers.find(member => 
        member.email === user?.email || 
        member.username === user?.username ||
        member.label === user?.username ||
        member.label === user?.email
      );
      
      if (foundMember) {
        const memberIdToSet = foundMember.id;
        console.log('✅ Found current user in NEW teamspace members');
        console.log('   User:', usernameToMatch);
        console.log('   NEW member ID:', memberIdToSet, '(this is the member instance for teamspace', formTeamspaceId, ')');
        console.log('   Member details:', foundMember);
        
        // CRITICAL: Set member to the NEW teamspace's member ID (different from old teamspace)
        setFormData(prev => {
          const oldMemberIds = prev.member;
          console.log('📝 UPDATING member field:');
          console.log('   OLD member IDs (from previous teamspace):', oldMemberIds);
          console.log('   NEW member ID (from current teamspace):', memberIdToSet);
          console.log('   Teamspace:', formTeamspaceId);

          // Always update, even if IDs look similar (they're different instances)
          return {
            ...prev,
            member: [String(memberIdToSet)] // Use member ID from NEW teamspace
          };
        });
        
        // Reset flag after successful update
        teamspaceJustChangedRef.current = false;
        prevTeamspaceForMemberRef.current = formTeamspaceId; // Update ref
      } else {
        console.warn('⚠️ Could not find current user in new teamspace members');
        console.warn('   User:', usernameToMatch);
        console.warn('   Available members:', formMembers.map(m => ({ label: m.label, id: m.id, email: m.email })));
        teamspaceJustChangedRef.current = false;
        prevTeamspaceForMemberRef.current = formTeamspaceId; // Update ref even if not found
      }
    } else if ((teamspaceJustChangedRef.current || teamspaceChanged) && formLookupLoading) {
      console.log('⏳ Waiting for NEW teamspace members to load. Teamspace:', formTeamspaceId);
    } else if ((teamspaceJustChangedRef.current || teamspaceChanged) && !membersBelongToCurrentTeamspace) {
      console.log('⏳ Waiting for members to load for current teamspace');
      console.log('   Current teamspace:', formTeamspaceId);
      console.log('   Members loaded for:', teamspaceIdWhenMembersLoadedRef.current);
    }
    
    // Update refs
    prevFormMembersLengthRef.current = formMembers.length;
  }, [formLookupLoading, formMembers, user, formTeamspaceId]);

  // Effect to set member to current user when members first load (on initial form open)
  const prevCurrentUserMemberRef = useRef(null);
  useEffect(() => {
    // Only set if: form is open, member is empty, currentUserMember just became available, and we didn't just change teamspace
    const memberIsEmpty = !formData.member || (Array.isArray(formData.member) && formData.member.length === 0);
    if (open && memberIsEmpty && currentUserMember && !prevCurrentUserMemberRef.current && !teamspaceJustChangedRef.current) {
      setFormData(prev => ({
        ...prev,
        member: [currentUserMember.id]
      }));
    }
    prevCurrentUserMemberRef.current = currentUserMember;
  }, [open, currentUserMember, formData.member]);
  
  // Initialize prevDisplayTimezoneRef when displayTimezone is first set
  useEffect(() => {
    if (displayTimezone && !prevDisplayTimezoneRef.current) {
      prevDisplayTimezoneRef.current = displayTimezone;
    }
  }, [displayTimezone]);
  
  // Effect to convert times when displayTimezone changes due to teamspace change
  // This runs after members are loaded for the new teamspace
  useEffect(() => {
    // Only convert if:
    // 1. Form is open
    // 2. Teamspace just changed (flag is set)
    // 3. use_device_timezone is False
    // 4. displayTimezone actually changed
    // 5. Members are loaded (displayTimezone is valid)
    if (open && 
        teamspaceJustChangedRef.current && 
        !userPreferences?.use_device_timezone &&
        prevDisplayTimezoneRef.current &&
        displayTimezone &&
        prevDisplayTimezoneRef.current !== displayTimezone &&
        formData.start_time &&
        formData.start_time.isValid() &&
        formData.date &&
        formData.date.isValid()) {
      
      const oldTimezone = prevDisplayTimezoneRef.current;
      const newTimezone = displayTimezone;
      
      console.log('🕐 Converting times after teamspace change:', oldTimezone, '->', newTimezone);
      
      setFormData(prev => {
        const updated = { ...prev };
        
        // Convert start_time: preserve wall clock time but in new timezone
        try {
          // Create full datetime in old timezone
          const oldDateTimeStr = `${prev.date.format('YYYY-MM-DD')}T${prev.start_time.format('HH:mm:ss')}`;
          // If old timezone is UTC, parse as UTC; otherwise use timezone
          const oldDateTime = (!oldTimezone || oldTimezone === 'UTC') 
            ? dayjs.utc(oldDateTimeStr)
            : dayjs.tz(oldDateTimeStr, oldTimezone);
          
          // Convert to UTC then to new timezone
          const utcDateTime = oldDateTime.utc();
          // If new timezone is UTC, keep as UTC; otherwise convert to new timezone
          const newDateTime = (!newTimezone || newTimezone === 'UTC')
            ? utcDateTime
            : utcDateTime.tz(newTimezone);
          
          // Extract just the time portion
          updated.start_time = dayjs(newDateTime.format('HH:mm:ss'), 'HH:mm:ss');
          console.log('   Start time converted:', prev.start_time.format('HH:mm'), '->', updated.start_time.format('HH:mm'));
        } catch (e) {
          console.warn('Error converting start_time:', e);
        }
        
        // Convert end_time: preserve wall clock time but in new timezone
        if (prev.end_time && prev.end_time.isValid()) {
          try {
            // Create full datetime in old timezone
            const oldDateTimeStr = `${prev.date.format('YYYY-MM-DD')}T${prev.end_time.format('HH:mm:ss')}`;
            // If old timezone is UTC, parse as UTC; otherwise use timezone
            const oldDateTime = (!oldTimezone || oldTimezone === 'UTC')
              ? dayjs.utc(oldDateTimeStr)
              : dayjs.tz(oldDateTimeStr, oldTimezone);
            
            // Convert to UTC then to new timezone
            const utcDateTime = oldDateTime.utc();
            // If new timezone is UTC, keep as UTC; otherwise convert to new timezone
            const newDateTime = (!newTimezone || newTimezone === 'UTC')
              ? utcDateTime
              : utcDateTime.tz(newTimezone);
            
            // Extract just the time portion
            updated.end_time = dayjs(newDateTime.format('HH:mm:ss'), 'HH:mm:ss');
            console.log('   End time converted:', prev.end_time.format('HH:mm'), '->', updated.end_time.format('HH:mm'));
          } catch (e) {
            console.warn('Error converting end_time:', e);
          }
        }
        
        return updated;
      });
      
      // Update the ref after conversion
      prevDisplayTimezoneRef.current = displayTimezone;
    } else if (displayTimezone && prevDisplayTimezoneRef.current !== displayTimezone && !teamspaceJustChangedRef.current) {
      // Update ref even if not converting (e.g., when dialog first opens)
      prevDisplayTimezoneRef.current = displayTimezone;
    }
  }, [displayTimezone, open, userPreferences?.use_device_timezone, formData.start_time, formData.end_time, formData.date]);


  return (
    <>
    <Dialog open={open} onClose={handleClose} fullScreen={isPhone}>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ display: 'flex', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: { xs: 1, sm: 0 }, justifyContent: 'space-between', alignItems: 'center', color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
          <span>Add New Time Entry</span>
          <Box sx={{ position: 'relative' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="teamspace-select-label">Teamspace</InputLabel>
              <Select
                labelId="teamspace-select-label"
                id="teamspace-select"
                value={formTeamspaceId || ''}
                label="Teamspace"
                onChange={(e) => handleTeamspaceChange(e.target.value)}
                disabled={formLookupLoading}
                sx={{
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.info.main + '40',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.info.main + '80',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.info.main,
                  },
                  '& .MuiSvgIcon-root': {
                    color: theme.palette.text.primary,
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: theme.palette.background.default,
                      color: theme.palette.text.primary,
                    },
                  },
                }}
              >
                {teamspaces.map((teamspace) => (
                  <MenuItem key={teamspace.id} value={teamspace.id}>
                    {teamspace.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Loading overlay for teamspace selector */}
            {formLookupLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  zIndex: 1000,
                  borderRadius: 1,
                }}
              >
                <CircularProgress size={40} sx={{ color: theme.palette.info.main }} />
              </Box>
            )}
          </Box>
        </DialogTitle>
          <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
            {successMessage && (
              <div style={{ color: theme.palette.success.main, fontWeight: 600, marginBottom: 8 }}>
                {successMessage}
              </div>
            )}
            {error && (
              <div style={{ color: theme.palette.error.main, fontWeight: 600, marginBottom: 8 }}>
                {error}
              </div>
            )}
            
            <DescriptionSuggestField
              autoFocus
              bulbOnly
              margin="dense"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              variant="standard"
              suggestions={descriptionSuggestions}
              tagSuggestions={tagSuggestions}
              onSelect={(desc) => setFormData(prev => ({ ...prev, description: desc }))}
              sx={{
                p:1,
                '& .MuiInputBase-input': {
                  color: theme.palette.text.primary,
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.text.secondary,
                },
                '& .MuiInput-underline:before': {
                  borderBottomColor: theme.palette.info.main + '40',
                },
                '&:hover .MuiInput-underline:before': {
                  borderBottomColor: theme.palette.info.main + '80',
                },
                '& .MuiInput-underline:after': {
                  borderBottomColor: theme.palette.info.main,
                },
              }}
            />
            
            {/* Icon buttons arranged horizontally with wrapping */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
              {/* Member, Project, Tag fields container with loading overlay */}
              <Box sx={{ position: 'relative', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                {/* Member Field with Icon */}
              <>
              {(!formData.member || (Array.isArray(formData.member) && formData.member.length === 0)) && (
                <IconButton
                  onClick={() => toggleField('member')}
                  sx={{ color: theme.palette.text.primary }}
                  disabled={formLookupLoading}
                >
                  <PersonIcon />
                </IconButton>
              )}
              {formData.member && Array.isArray(formData.member) && formData.member.length > 0 && (
                <Chip
                  icon={<PersonIcon sx={{ color: theme.palette.text.primary }} />}
                  label={
                    getSelectedLabel('member', memberOptions, formData.member) ||
                    (formData.member.length === 1 ? (currentUserMember?.label || user?.username || user?.email_alt) : `(${formData.member.length} members)`)
                  }
                  onClick={() => canCreateForOthers && !formLookupLoading && toggleField('member')}
                  onDelete={canCreateForOthers && !formLookupLoading ? () => {
                    setFormData(prev => ({ ...prev, member: [] }));
                    setExpandedFields(prev => ({ ...prev, member: false }));
                  } : undefined}
                  variant="outlined"
                  sx={{
                    cursor: !canCreateForOthers || formLookupLoading ? 'default' : 'pointer',
                    borderColor: theme.palette.info.main + '4D',
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.info.main + '1A',
                    opacity: canCreateForOthers ? 1 : 0.7
                  }}
                />
              )}
              {validationErrors.member && (
                <Typography variant="caption" color="error">
                  {validationErrors.member}
                </Typography>
              )}
            </>

              {/* Project Field with Icon */}
              <>
                {!formData.project && !formData.category && (
                  <IconButton
                    onClick={() => toggleField('project')}
                    sx={{ color: theme.palette.text.primary }}
                    disabled={formLookupLoading}
                  >
                    <FolderIcon />
                  </IconButton>
                )}
                {(formData.project || formData.category) && (
                  <Chip 
                    icon={<FolderIcon sx={{ color: theme.palette.text.primary }} />}
                    label={getSelectedLabel('project', availableProjects, formData.project)}
                    onClick={() => !formLookupLoading && toggleField('project')}
                    onDelete={!formLookupLoading ? () => {
                      setFormData(prev => ({ ...prev, project: '', category: '' }));
                      setExpandedFields(prev => ({ ...prev, project: false }));
                      setBillableSetByProject(false);
                      setBillableOverriddenFromProject(false);
                    } : undefined}
                    variant="outlined"
                    sx={{ cursor: formLookupLoading ? 'default' : 'pointer', borderColor: theme.palette.info.main + '4D', color: theme.palette.text.primary, backgroundColor: theme.palette.info.main + '1A' }}
                  />
                )}
                {validationErrors.project && (
                  <Typography variant="caption" color="error">
                    {validationErrors.project}
                  </Typography>
                )}
              </>

              {/* Tag Field with Icon */}
              <>
                {(!formData.tag || (Array.isArray(formData.tag) && formData.tag.length === 0)) && (
                  <IconButton 
                    onClick={() => toggleField('tag')}
                    sx={{ color: theme.palette.text.primary }}
                    disabled={formLookupLoading}
                  >
                    <LocalOfferIcon />
                  </IconButton>
                )}
                {formData.tag && Array.isArray(formData.tag) && formData.tag.length > 0 && (
                  <Chip 
                    icon={<LocalOfferIcon sx={{ color: theme.palette.text.primary }} />}
                    label={getSelectedLabel('tag', tagOptions, formData.tag)}
                    onClick={() => !formLookupLoading && toggleField('tag')}
                    onDelete={!formLookupLoading ? () => {
                      setFormData(prev => ({
                        ...prev,
                        tag: [],
                        description: syncDescriptionWithTags({
                          description: prev.description,
                          previousTagIds: prev.tag,
                          nextTagIds: [],
                          tagLookup: formTagLookup,
                        }),
                      }));
                      setExpandedFields(prev => ({ ...prev, tag: false }));
                    } : undefined}
                    variant="outlined"
                    sx={{ cursor: formLookupLoading ? 'default' : 'pointer', borderColor: theme.palette.info.main + '4D', color: theme.palette.text.primary, backgroundColor: theme.palette.info.main + '1A' }}
                  />
                )}
                {validationErrors.tag && (
                  <Typography variant="caption" color="error">
                    {validationErrors.tag}
                  </Typography>
                )}
              </>
              {/* Loading overlay for member, project, and tag fields */}
              {formLookupLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    zIndex: 1000,
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress size={40} sx={{ color: theme.palette.info.main }} />
                </Box>
              )}
              </Box>

              {/* Billable Field with Icon */}
              <>
                <Tooltip title={formData.category ? 'Categories are non-billable' : ''}>
                  <span>
                    <IconButton
                      disabled={Boolean(formData.category)}
                      onClick={() => {
                        if (billableSetByProject) setBillableOverriddenFromProject(true);
                        setFormData(prev => ({ ...prev, billable: !prev.billable }));
                        setBillableSetByProject(false);
                      }}
                      sx={{
                        color: (formData.billable && !formData.category) ? '#FFD700' : theme.palette.text.primary,
                        ...(billableSetByProject && {
                          boxShadow: '0 0 0 2px #f44336',
                        }),
                      }}
                      size="small"
                    >
                      <Typography sx={{ fontSize: '1rem', fontWeight: (formData.billable && !formData.category) ? 'bold' : 'normal' }}>
                        $
                      </Typography>
                    </IconButton>
                  </span>
                </Tooltip>
                {billableOverriddenFromProject && (
                  <Typography variant="caption" sx={{ color: 'warning.main' }}>
                    Billable changed from project default
                  </Typography>
                )}
              </>
            </Box>

            {/* Expandable Autocomplete fields */}
            {expandedFields.member && (
              <Autocomplete
                multiple
                required
                disabled={!canCreateForOthers}
                disablePortal
                options={memberOptions}
                loading={formLookupLoading}
                open={expandedFields.member}
                onClose={(event, reason) => {
                  // Only close on blur or escape, not on selection
                  if (reason === 'blur' || reason === 'escape') {
                    setExpandedFields(prev => ({ ...prev, member: false }));
                  }
                }}
                value={Array.isArray(formData.member) && formData.member.length > 0
                  ? memberOptions.filter(option => formData.member.map(String).includes(String(option.id)))
                  : []}
                onChange={canCreateForOthers ? handleAutocompleteChange('member') : undefined}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                filterOptions={(options, state) =>
                  options.filter(option => {
                    const l = (option && option.label) ? String(option.label) : '';
                    const q = (state && state.inputValue) ? String(state.inputValue) : '';
                    return l.toLowerCase().includes(q.toLowerCase());
                  })
                }
                sx={{ width: '100%', mb: 1 }}
                renderInput={(params) => <TextField {...params} label="Member" autoFocus disabled={!canCreateForOthers}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& fieldset': {
                      borderColor: theme.palette.info.main + '4D',
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.info.main + '80',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.info.main,
                    },
                  }}/>}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.label}
                      size="small"
                    />
                  ))
                }
              />
            )}
            {expandedFields.project && (
            <Autocomplete
              disablePortal
              options={projectAndCategoryOptions}
              loading={formLookupLoading}
                open
                onClose={() => setExpandedFields(prev => ({ ...prev, project: false }))}
              value={
                formData.category 
                  ? projectAndCategoryOptions.find(option => option.id === formData.category && option.isCategory) || null
                  : (formData.project 
                      ? projectAndCategoryOptions.find(option => option.id === formData.project && !option.isCategory) || null
                      : null)
              }
              onChange={handleAutocompleteChange('project')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) =>
                  options.filter(option => {
                    const l = (option && option.label) ? String(option.label) : '';
                    const q = (state && state.inputValue) ? String(state.inputValue) : '';
                    return l.toLowerCase().includes(q.toLowerCase());
                  })
                }
                sx={{ width: '100%', mb: 1 }}
                renderInput={(params) => <TextField {...params} label="Project" autoFocus 
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& fieldset': {
                      borderColor: theme.palette.info.main + '4D',
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.info.main + '80',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.info.main,
                    },
                  }}/>}
              />
            )}
            {expandedFields.tag && (
            <Autocomplete
              multiple
              disablePortal
              options={tagOptions}
              loading={formLookupLoading}
              open={expandedFields.tag}
              onClose={(event, reason) => {
                // Only close on blur or escape, not on selection
                if (reason === 'blur' || reason === 'escape') {
                  setExpandedFields(prev => ({ ...prev, tag: false }));
                }
              }}
              value={Array.isArray(formData.tag) && formData.tag.length > 0
                ? tagOptions.filter(option => formData.tag.includes(option.id))
                : []}
              onChange={handleAutocompleteChange('tag')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) =>
                  options.filter(option =>
                    option.label.toLowerCase().includes(state.inputValue.toLowerCase())
                  )
                }
                sx={{ width: '100%', mb: 1 }}
                renderInput={(params) => <TextField {...params} label="Tags (optional)" autoFocus 
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& fieldset': {
                      borderColor: theme.palette.info.main + '4D',
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.info.main + '80',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.info.main,
                    },
                  }}/>}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.id}
                      label={option.label}
                      size="small"
                    />
                  ))
                }
              />
            )}
            
            {/* Date and Time Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Date"
                  type="date"
                  value={formData.date ? formData.date.format('YYYY-MM-DD') : ''}
                  onChange={(e) => {
                    const newDate = dayjs(e.target.value);
                    if (newDate.isValid()) {
                      setFormData(prev => {
                        const updated = { ...prev, date: newDate };
                        // If end_time exists and start_time exists, update end_time to keep same duration
                        if (updated.end_time && updated.end_time.isValid() && updated.start_time && updated.start_time.isValid()) {
                          const oldStartDateTime = prev.date
                            .hour(prev.start_time.hour())
                            .minute(prev.start_time.minute())
                            .second(0);
                          const oldEndDateTime = prev.end_time;
                          const durationMinutes = oldEndDateTime.diff(oldStartDateTime, 'minute');
                          
                          const newStartDateTime = newDate
                            .hour(updated.start_time.hour())
                            .minute(updated.start_time.minute())
                            .second(0);
                          updated.end_time = newStartDateTime.add(durationMinutes, 'minute');
                        }
                        return updated;
                      });
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  margin="dense"
                  error={!!validationErrors.date}
                  helperText={validationErrors.date}
                  sx={{ flex: 2,
                    '& .MuiInputBase-input': {
                      color: theme.palette.text.primary,
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiInput-underline:before': {
                      borderBottomColor: theme.palette.info.main + '40',
                    },
                    '&:hover .MuiInput-underline:before': {
                      borderBottomColor: theme.palette.info.main + '80',
                    },
                    '& .MuiInput-underline:after': {
                      borderBottomColor: theme.palette.info.main,
                    },
                    '& input[type="date"]::-webkit-calendar-picker-indicator': {
                      filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none',
                    },
                  }}
                />
                {/* Timezone Field - Beside date picker */}
                <TextField
                  label="Timezone"
                  value={displayTimezone || 'UTC'}
                  InputProps={{
                    readOnly: true,
                  }}
                  margin="dense"
                  variant="outlined"
                  sx={{ 
                    flex: 1,
                    '& .MuiInputBase-input': {
                      color: theme.palette.text.secondary,
                      cursor: 'default',
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.info.main + '40',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.info.main + '80',
                    },
                    '& .MuiOutlinedInput-root.Mui-disabled': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.info.main + '40',
                      },
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileTimePicker 
                    label="Start Time"
                    value={formData.start_time}
                    onChange={handleDateTimeChange('start_time')}
                    ampm
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'dense',
                        required: true,
                        error: !!validationErrors.start_time,
                        helperText: validationErrors.start_time,
                        inputProps: { 
                          inputMode: 'numeric',
                          pattern: '[0-9: ]*',
                          placeholder: 'HH:MM AM/PM'
                        },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                          },
                          '& .MuiInputLabel-root': {
                            color: theme.palette.text.secondary,
                          },
                          '& fieldset': {
                            borderColor: theme.palette.info.main + '4D',
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.info.main + '80',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.info.main,
                          },
                        }
                      },
                      actionBar: {
                        actions: ['cancel', 'accept'],
                        sx: {
                          '& .MuiButtonBase-root': {
                            color: theme.palette.text.primary,
                          }
                        }
                      }
                    }}
                    sx={{ flex: 1 }}
                  />
                  <MobileTimePicker 
                    label="End Time (optional)"
                    value={formData.end_time}
                    onChange={handleDateTimeChange('end_time')}
                    ampm
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        margin: 'dense',
                        error: !!validationErrors.end_time,
                        inputProps: { 
                          inputMode: 'numeric',
                          pattern: '[0-9: ]*',
                          placeholder: 'HH:MM AM/PM'
                        },
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                          },
                          '& .MuiInputLabel-root': {
                            color: theme.palette.text.secondary,
                          },
                          '& fieldset': {
                            borderColor: theme.palette.info.main + '4D',
                          },
                          '&:hover fieldset': {
                            borderColor: theme.palette.info.main + '80',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: theme.palette.info.main,
                          },
                        }
                      },
                      actionBar: {
                        actions: ['cancel', 'accept'],
                        sx: {
                          '& .MuiButtonBase-root': {
                            color: theme.palette.text.primary,
                          }
                        }
                      }
                    }}
                    sx={{ flex: 1 }}
                  />
                </LocalizationProvider>
              </Box>
              {formData.start_time && formData.start_time.isValid() && 
               formData.end_time && formData.end_time.isValid() && 
               (() => {
                 const startDateTime = formData.date
                   .hour(formData.start_time.hour())
                   .minute(formData.start_time.minute())
                   .second(0);
                 const endDateTime = formData.date
                   .hour(formData.end_time.hour())
                   .minute(formData.end_time.minute())
                   .second(0);
                 return endDateTime.isBefore(startDateTime);
               })() && (
                <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5, ml: 1.75 }}>
                  This will span to the next day
                </Typography>
              )}
            </Box>
            
            {/* Duration (Editable, auto-calculated from start and end time) */}
            <Box sx={{ mt: 2, mb: 2, px: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Typography sx={{ flexGrow: 1, pt: 1 }}>
                  Duration (HH:MM) (optional)
                </Typography>
                <TextField
                  type="text"
                  inputRef={durationInputRef}
                  value={durationInputValue !== null ? durationInputValue : minutesToHHMM(formData.duration_minutes)}
                  onChange={(e) => {
                    // Handle all input including deletion/backspace
                    const inputValue = e.target.value;
                    const input = e.target;
                    const cursorPosition = input.selectionStart;
                    
                    // Always update the display value to allow deletion
                    setDurationInputValue(inputValue);
                    durationPrevValueRef.current = inputValue;
                    
                    // Check if hours > 24
                    const hours = getHoursFromHHMM(inputValue);
                    if (inputValue && inputValue.includes(':') && hours > 24) {
                      setDurationError('Hours cannot exceed 24');
                      // Don't update formData if hours > 24, just keep the input value
                    } else {
                      // Check if total minutes > 1440 (24 hours)
                      if (inputValue && inputValue.includes(':')) {
                        const minutes = HHMMToMinutes(inputValue);
                        if (minutes > 1440) {
                          setDurationError('Duration cannot exceed 24 hours (1440 minutes)');
                          // Don't update formData if minutes > 1440, just keep the input value
                        } else {
                          setDurationError('');
                          // Update formData only if within valid range
                          const clampedValue = Math.min(Math.max(minutes, 0), 1440);
                          setFormData(prev => {
                            const updated = { ...prev, duration_minutes: clampedValue };
                            // If start_time exists, calculate end_time
                            if (updated.start_time && updated.start_time.isValid() && clampedValue > 0) {
                              const startDateTime = updated.date
                                .hour(updated.start_time.hour())
                                .minute(updated.start_time.minute())
                                .second(0);
                              updated.end_time = startDateTime.add(clampedValue, 'minute');
                            } else {
                              updated.end_time = null;
                            }
                            return updated;
                          });
                        }
                      } else {
                        setDurationError('');
                      }
                    }
                    
                    // Preserve cursor position after React updates
                    setTimeout(() => {
                      if (durationInputRef.current) {
                        // Use the exact cursor position from the input event
                        const newPosition = Math.min(cursorPosition, inputValue.length);
                        durationInputRef.current.setSelectionRange(newPosition, newPosition);
                      }
                    }, 0);
                  }}
                  onBlur={(e) => {
                    const inputValue = e.target.value.trim();
                    const hours = getHoursFromHHMM(inputValue);
                    
                    // Check if hours > 24 before processing
                    if (inputValue && inputValue.includes(':') && hours > 24) {
                      setDurationError('Hours cannot exceed 24');
                      // Don't normalize if there's an error, keep the input as-is
                      return;
                    }
                    
                    // Check if total minutes > 1440 before processing
                    if (inputValue && inputValue.includes(':')) {
                      const minutes = HHMMToMinutes(inputValue);
                      if (minutes > 1440) {
                        setDurationError('Duration cannot exceed 24 hours (1440 minutes)');
                        // Don't normalize if there's an error, keep the input as-is
                        return;
                    }}
                    
                    setDurationError('');
                    
                    if (inputValue === '') {
                      // If empty on blur, default to 15 minutes (00:06)
                      const defaultMinutes = 15;
                      setDurationInputValue(null);
                      setFormData(prev => {
                        const updated = { ...prev, duration_minutes: defaultMinutes };
                        // If start_time exists, calculate end_time
                        if (updated.start_time && updated.start_time.isValid()) {
                          const startDateTime = updated.date
                            .hour(updated.start_time.hour())
                            .minute(updated.start_time.minute())
                            .second(0);
                          updated.end_time = startDateTime.add(defaultMinutes, 'minute');
                        }
                        return updated;
                      });
                    } else {
                      // Normalize on blur: convert any MM value to proper HH:MM
                      // e.g., 01:80 becomes 02:20 automatically
                      const minutes = HHMMToMinutes(inputValue);
                    const clampedValue = Math.min(Math.max(minutes, 0), 1440); // 24 hours max
                      const normalizedValue = minutesToHHMM(clampedValue);
                      setDurationInputValue(null);
                      handleDurationChange(normalizedValue);
                    }
                  }}
                  placeholder="HH:MM"
                  inputProps={{ 
                    pattern: '[0-9]{1,2}:[0-9]{1,}',
                    inputMode: 'numeric'
                  }}
                  size="small"
                  error={!!validationErrors.duration_minutes || !!durationError}
                  sx={{ width: 120,
                    '& .MuiInputBase-input': {
                      color: theme.palette.text.primary,
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.secondary,
                    },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: theme.palette.background.paper,
                    },
                    '& fieldset': {
                      borderColor: theme.palette.info.main + '40',
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.info.main + '80',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.info.main,
                    },
                  }}
                />
              </Box>
              {(durationError || validationErrors.duration_minutes) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                  <Box>
                    {durationError && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {durationError}
                      </Typography>
                    )}
                    {validationErrors.duration_minutes && !durationError && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}>
                {validationErrors.duration_minutes}
              </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Notes Field */}
            <Box sx={{ mt: 1, mb: 1, px: 1 }}>
              <TextField
                label="Notes (optional)"
                name="notes"
                value={formData.notes || ''}
                onChange={handleChange}
                multiline
                minRows={3}
                fullWidth
                inputProps={{ maxLength: 100 }}
                helperText={`${(formData.notes || '').length}/100`}
                sx={{
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper,
                  },
                  '& fieldset': {
                    borderColor: theme.palette.info.main + '40',
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.info.main + '80',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.info.main,
                  },
                  '& .MuiFormHelperText-root': {
                    color: theme.palette.text.secondary,
                    textAlign: 'right',
                  },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ backgroundColor: theme.palette.background.default, flexWrap: { xs: 'wrap', sm: 'nowrap' }, rowGap: { xs: 1, sm: 0 } }}>
            <Button onClick={handleClose} disabled={isSubmitting} variant="contained" sx={{ bgcolor: theme.palette.error.main, color: theme.palette.error.contrastText, '&:hover': { bgcolor: theme.palette.error.dark } }}>Cancel</Button>
            <Button
              type='button'
              onClick={handleSaveAndAddAnother}
              disabled={isSubmitting}
              variant="outlined"
              sx={{ color: theme.palette.text.primary, borderColor: theme.palette.info.main, '&:hover': { borderColor: theme.palette.info.dark, backgroundColor: theme.palette.info.main + '14' } }}
            >
              Save and add another
            </Button>
            <Button type='submit' disabled={isSubmitting} variant="contained" sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}>Save</Button>
          </DialogActions>
      </form>
    </Dialog>
    
    {/* Duplicate entry confirmation dialog */}
    <Dialog open={duplicateCheckDialogOpen} onClose={() => setDuplicateCheckDialogOpen(false)}>
      <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
        Duplicate Time {duplicateCheckInfo?.duplicateMemberIds?.length > 1 ? 'Entries' : 'Entry'} Detected
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
        <Typography sx={{ color: theme.palette.text.primary, mb: 1 }}>
          The following member{duplicateCheckInfo?.duplicateMemberIds?.length > 1 ? 's' : ''} already {duplicateCheckInfo?.duplicateMemberIds?.length > 1 ? 'have' : 'has'} a time entry with the same start time, end time, project, category, and description:
        </Typography>
        <Box sx={{ mb: 2 }}>
          {duplicateCheckInfo?.duplicateMemberIds?.map(id => (
            <Chip
              key={id}
              label={formMemberLookup[String(id)] || String(id)}
              size="small"
              sx={{ mr: 0.5, mb: 0.5, backgroundColor: theme.palette.warning.main + '33', color: theme.palette.text.primary }}
            />
          ))}
        </Box>
        {duplicateCheckInfo?.nonDuplicateMemberIds?.length > 0 && (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {duplicateCheckInfo.nonDuplicateMemberIds.length} member{duplicateCheckInfo.nonDuplicateMemberIds.length > 1 ? 's' : ''} without duplicates will still be created if you choose "Skip Duplicates".
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
        <Button
          onClick={() => setDuplicateCheckDialogOpen(false)}
          variant="outlined"
          sx={{ color: theme.palette.text.primary, borderColor: theme.palette.info.main, '&:hover': { borderColor: theme.palette.info.dark } }}
        >
          Cancel
        </Button>
        {duplicateCheckInfo?.nonDuplicateMemberIds?.length > 0 && (
          <Button
            onClick={() => {
              setDuplicateCheckDialogOpen(false);
              doCreateEntries(duplicateCheckInfo.nonDuplicateMemberIds, duplicateCheckInfo.basePayload, duplicateCheckInfo.keepOpen);
            }}
            variant="contained"
            sx={{ bgcolor: theme.palette.warning.main, color: theme.palette.warning.contrastText, '&:hover': { bgcolor: theme.palette.warning.dark } }}
          >
            Skip Duplicates
          </Button>
        )}
        <Button
          onClick={() => {
            setDuplicateCheckDialogOpen(false);
            doCreateEntries(duplicateCheckInfo.allMemberIds, duplicateCheckInfo.basePayload, duplicateCheckInfo.keepOpen);
          }}
          variant="contained"
          sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}
        >
          Create All Anyway
        </Button>
      </DialogActions>
    </Dialog>

    {/* Error dialog for API failures after optimistic update */}
    <Dialog open={apiErrorDialogOpen} onClose={() => setApiErrorDialogOpen(false)}>
      <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>
        Error Creating Time Entry
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
        <Typography sx={{ color: theme.palette.text.primary, mb: 2 }}>
          There was an issue with creating the time entry. The entry may have been added to the table, but it may not have been saved correctly.
        </Typography>
        <Typography sx={{ color: theme.palette.text.primary }}>
          Please reload the page and try again.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
        <Button 
          onClick={() => window.location.reload()} 
          variant="contained" 
          sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText, '&:hover': { bgcolor: theme.palette.primary.dark } }}
        >
          Reload Page
        </Button>
        <Button 
          onClick={() => setApiErrorDialogOpen(false)} 
          variant="outlined"
          sx={{ color: theme.palette.text.primary, borderColor: theme.palette.info.main, '&:hover': { borderColor: theme.palette.info.dark } }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Confirmation dialog for teamspace change */}
    <Dialog open={showTeamspaceChangeDialog} onClose={handleCancelTeamspaceChange}>
      <DialogTitle sx={{ color: theme.palette.text.primary, backgroundColor: theme.palette.background.default }}>Confirm Teamspace Change</DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.palette.background.default }}>
        <Typography sx={{ color: theme.palette.text.primary }}>
          Changing the teamspace will reset the Member, Project, Category, and Tag fields. 
          All other fields (description, billable, date, time, duration) will be preserved.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
        <Button onClick={handleCancelTeamspaceChange} variant="contained" sx={{ bgcolor: theme.palette.error.main, color: theme.palette.error.contrastText, '&:hover': { bgcolor: theme.palette.error.dark } }}>
          Cancel
        </Button>
        <Button onClick={handleConfirmTeamspaceChange} variant="contained" sx={{ bgcolor: theme.palette.success.main, color: theme.palette.success.contrastText, '&:hover': { bgcolor: theme.palette.success.dark } }}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AddTimeEntryForm;